import { useQuery } from '@apollo/client/react'
import type { ErrorLike } from '@apollo/client'

import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'

import {
  buildAllChannelsInsightsInput,
  isWaitingForInsightsPrerequisites,
} from './allChannelsInsightsVariables'
import type { AllChannelsInsightsOptions } from './allChannelsInsightsVariables'

// Summary cards + performance table only. Deliberately omits `channelTimeSeries`
// so this query doesn't wait on the slow time-series field resolver — the
// metrics graph fetches that separately via `useAllChannelsTimeSeries`.
export const AllChannelsInsightsSummaryDocument = graphql(/* GraphQL */ `
  query AllChannelsInsightsSummary($input: AllChannelsPostInsightsInput!) {
    allChannelsPostInsights(input: $input) {
      summary {
        type
        unit
        name
        description
        value
        difference
        differenceMode
      }
      channelInsights {
        channel {
          id
          name
          avatar
          service
        }
        metrics {
          type
          unit
          displayName
          description
          value
          difference
        }
      }
    }
  }
`)

export type AllChannelsInsightsSummaryResult = ResultOf<
  typeof AllChannelsInsightsSummaryDocument
>

type AllChannelsInsightsSummaryData =
  AllChannelsInsightsSummaryResult['allChannelsPostInsights']

export type SummaryMetric = AllChannelsInsightsSummaryData['summary'][number]
export type ChannelInsight =
  AllChannelsInsightsSummaryData['channelInsights'][number]

interface UseAllChannelsInsightsSummaryResult {
  summary: SummaryMetric[]
  channelInsights: ChannelInsight[]
  loading: boolean
  error: ErrorLike | undefined
}

export function useAllChannelsInsightsSummary(
  options: AllChannelsInsightsOptions,
): UseAllChannelsInsightsSummaryResult {
  const isWaitingForPrerequisites = isWaitingForInsightsPrerequisites(options)
  const { data, previousData, loading, error } = useQuery(
    AllChannelsInsightsSummaryDocument,
    {
      variables: { input: buildAllChannelsInsightsInput(options) },
      fetchPolicy: 'cache-first',
      skip: options.skip || isWaitingForPrerequisites,
    },
  )

  // Use previous data while refetching to avoid layout jumps.
  const effectiveData = data ?? previousData
  const result = effectiveData?.allChannelsPostInsights

  return {
    summary: result?.summary ?? [],
    channelInsights: result?.channelInsights ?? [],
    loading: loading || isWaitingForPrerequisites,
    error,
  }
}
